// Funnel-generation approval form — the in-product recovery for a funnel that
// loud-failed with funnel_generation_poc_preset_unresolved (OGC-209). An owner
// or admin supplies the POC presentation (identity, quiz copy, qualification,
// logo, and — for financing — human-authored payment/legal content) and it is
// POSTed to /api/saas/campaigns/:campaignId/funnel-generation-approval. The
// field contract mirrors sanitizeFunnelGenerationApprovalInput on the server.
//
// Financial terms, payment examples, and legal disclosures are ENTERED BY THE
// USER. Nothing on this screen fabricates financing content — that is the whole
// reason financing stays approval-gated.
import { useEffect, useMemo, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { api } from '../api.js';
import { Button } from '../components/primitives.jsx';
import { safeFailureMessage } from '../safe-failure.js';

const QUIZ_PRESET = 'poc_generated_quiz_v1';
const FINANCING_PRESET = 'poc_financing_v1';

const DEFAULT_TRACKING = {
  events: [
    'page_view', 'cta_clicked', 'quiz_started', 'quiz_step_completed', 'quiz_completed',
    'lead_submitted', 'booking_opened', 'booking_completed',
  ],
  attributionFields: ['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'gclid'],
  modules: ['platform_analytics'],
};

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

// Read a File into a bare base64 string (no data: prefix) for the logo upload
// route, which accepts base64 JSON to reuse the standard request parser.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('file_read_failed'));
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function cityLabel(city) {
  return String(city || '').split(/[+,]/)[0].trim();
}

function lines(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

// Build the presetForOffer-equivalent mapping on the client so the form knows
// which preset the failed campaign needs.
function presetForOffer(offer) {
  const slug = String(offer || '').trim().toLowerCase();
  if (slug === 'quiz-qualifier') return QUIZ_PRESET;
  if (slug === 'financed-offer') return FINANCING_PRESET;
  return QUIZ_PRESET;
}

function emptyQuizState(campaign) {
  const city = cityLabel(campaign?.settings?.city || campaign?.brief?.serviceArea);
  return {
    phone: campaign?.settings?.phone || '',
    location: campaign?.settings?.city || campaign?.brief?.serviceArea || '',
    serviceArea: city || '',
    primaryLabel: 'Start my project plan',
    consentLabel: 'I agree to receive messages about this project inquiry. Message and data rates may apply. Reply STOP to unsubscribe.',
    seoTitle: '',
    seoDescription: '',
    projectScope: 'Full renovation\nPartial update\nStill deciding',
    timeline: 'Within 3 months\n3-6 months\nJust exploring',
    budgetBand: 'Budget range set\nBudget being planned\nNot sure yet',
    ruleField: 'budgetBand',
    ruleValue: 'Not sure yet',
    claims: '',
    logoArtifactId: '',
  };
}

function emptyFinancingState() {
  return {
    heroHeadline: '',
    heroBody: '',
    disclosure: '',
    paymentHeading: '',
    paymentBody: '',
    paymentTerms: '',
    // Payment examples: one "label | value | detail" per line, entered by the user.
    paymentExamples: '',
    benefits: '',
    // FAQs: "question | answer" per line.
    faqs: '',
    legalParagraphs: '',
  };
}

function parsePaymentExamples(raw) {
  return lines(raw).slice(0, 4).map((line, index) => {
    const [label, value, detail] = line.split('|').map((part) => (part || '').trim());
    return {
      label: label || '',
      value: value || '',
      detail: detail || '',
      featured: index === 0,
    };
  });
}

function parseFaqs(raw) {
  return lines(raw).slice(0, 12).map((line) => {
    const [question, answer] = line.split('|').map((part) => (part || '').trim());
    return { question: question || '', answer: answer || '' };
  });
}

function parseClaims(raw) {
  return lines(raw).slice(0, 20).map((line) => {
    const [label, type, source] = line.split('|').map((part) => (part || '').trim());
    return { label: label || '', type: type || 'benefit', source: source || '' };
  });
}

export function buildApprovalPayload({ preset, quiz, financing }) {
  const quizOptions = {
    projectScope: lines(quiz.projectScope),
    timeline: lines(quiz.timeline),
    budgetBand: lines(quiz.budgetBand),
  };
  const payload = {
    preset,
    identity: {
      phone: quiz.phone.trim(),
      location: quiz.location.trim(),
      serviceArea: lines(quiz.serviceArea.replace(/,/g, '\n')),
    },
    claims: parseClaims(quiz.claims),
    quizOptions,
    qualificationRules: [{
      fieldId: quiz.ruleField,
      operator: 'equals',
      value: quiz.ruleValue.trim(),
      outcome: 'not_qualified',
    }],
    assets: quiz.logoArtifactId
      ? [{ artifactId: quiz.logoArtifactId, role: 'logo', alt: 'Approved campaign logo' }]
      : [],
    cta: { primaryLabel: quiz.primaryLabel.trim() },
    booking: { providerSlot: 'tenant_booking' },
    consent: { label: quiz.consentLabel.trim(), required: true },
    seo: { title: quiz.seoTitle.trim(), description: quiz.seoDescription.trim() },
    tracking: DEFAULT_TRACKING,
  };
  if (preset === FINANCING_PRESET) {
    payload.financing = {
      heroHeadline: financing.heroHeadline.trim(),
      heroBody: financing.heroBody.trim(),
      disclosure: financing.disclosure.trim(),
      paymentHeading: financing.paymentHeading.trim(),
      paymentBody: financing.paymentBody.trim(),
      paymentTerms: financing.paymentTerms.trim(),
      paymentExamples: parsePaymentExamples(financing.paymentExamples),
      benefits: lines(financing.benefits),
      faqs: parseFaqs(financing.faqs),
      legalParagraphs: lines(financing.legalParagraphs),
    };
  }
  return payload;
}

export default function FunnelGenerationApprovalForm({
  companyId,
  campaignId,
  onClose,
  onApproved,
}) {
  const [campaign, setCampaign] = useState(null);
  const [images, setImages] = useState([]);
  const [loadState, setLoadState] = useState('loading');
  const [loadError, setLoadError] = useState(null);
  const [submitState, setSubmitState] = useState('idle');
  const [submitError, setSubmitError] = useState(null);
  const [uploadState, setUploadState] = useState('idle');
  const [uploadError, setUploadError] = useState(null);
  const [quiz, setQuiz] = useState(emptyQuizState(null));
  const [financing, setFinancing] = useState(emptyFinancingState());

  const preset = useMemo(() => presetForOffer(campaign?.offer), [campaign?.offer]);
  const isFinancing = preset === FINANCING_PRESET;
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Match the CampaignReview modal: focus on open and close on Escape.
  useEffect(() => {
    dialogRef.current?.focus();
    function onKeyDown(event) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoadState('loading');
    setLoadError(null);
    Promise.all([
      api.get(`/api/saas/campaigns/${campaignId}`, tenantHeaders(companyId)),
      api.get(`/api/saas/artifacts?campaignId=${encodeURIComponent(campaignId)}&artifactType=image&limit=100`, tenantHeaders(companyId))
        .catch(() => ({ artifacts: [] })),
    ])
      .then(([campaignResponse, artifactResponse]) => {
        if (!alive) return;
        const nextCampaign = campaignResponse.campaign || campaignResponse;
        const approvedLogos = (artifactResponse.artifacts || []).filter((artifact) => (
          artifact?.metadata?.approvalStatus === 'approved'
          && String(artifact?.metadata?.role || artifact?.metadata?.assetRole || artifact?.metadata?.usage || '')
            .toLowerCase() === 'logo'
        ));
        setCampaign(nextCampaign);
        setImages(approvedLogos);
        setQuiz((current) => ({
          ...emptyQuizState(nextCampaign),
          ...current,
          logoArtifactId: current.logoArtifactId || approvedLogos[0]?.id || '',
          phone: current.phone || nextCampaign?.settings?.phone || '',
          location: current.location || nextCampaign?.settings?.city || nextCampaign?.brief?.serviceArea || '',
          serviceArea: current.serviceArea || cityLabel(nextCampaign?.settings?.city || nextCampaign?.brief?.serviceArea),
        }));
        setLoadState('ready');
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err);
        setLoadState('error');
      });
    return () => { alive = false; };
  }, [companyId, campaignId]);

  async function uploadLogo(file) {
    if (!file || uploadState === 'uploading') return;
    setUploadState('uploading');
    setUploadError(null);
    try {
      const dataBase64 = await fileToBase64(file);
      const response = await api.post(
        `/api/saas/campaigns/${campaignId}/logo`,
        { contentType: file.type, dataBase64, alt: file.name },
        tenantHeaders(companyId),
      );
      const logo = response.logo;
      // Add the freshly uploaded logo to the picker and select it.
      setImages((current) => [
        { id: logo.artifactId, metadata: { role: 'logo', approvalStatus: 'approved', alt: logo.alt } },
        ...current,
      ]);
      setQuiz((current) => ({ ...current, logoArtifactId: logo.artifactId }));
      setUploadState('idle');
    } catch (err) {
      setUploadError(err);
      setUploadState('idle');
    }
  }

  async function submit() {
    if (submitState === 'submitting') return;
    setSubmitState('submitting');
    setSubmitError(null);
    try {
      const payload = buildApprovalPayload({ preset, quiz, financing });
      const response = await api.post(
        `/api/saas/campaigns/${campaignId}/funnel-generation-approval`,
        payload,
        tenantHeaders(companyId),
      );
      setSubmitState('done');
      if (onApproved) onApproved(response.approval || null);
    } catch (err) {
      setSubmitError(err);
      setSubmitState('idle');
    }
  }

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div style={panelStyle} ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Provide funnel presentation">
        <div style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Funnel recovery</div>
            <h2 style={titleStyle}>Provide funnel presentation</h2>
            <div style={{ fontSize: 13, color: PALETTE.ink3, marginTop: 4 }}>
              {isFinancing
                ? 'This financing funnel needs its human-approved payment and legal content before it can generate.'
                : 'This funnel needs its presentation details before it can generate.'}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>

        {loadState === 'loading' && <p style={mutedStyle}>Loading campaign…</p>}
        {loadState === 'error' && (
          <p style={{ ...mutedStyle, color: PALETTE.warn }}>{safeFailureMessage(loadError, 'campaign-review')}</p>
        )}

        {loadState === 'ready' && (
          <div style={{ display: 'grid', gap: 20 }}>
            <Section title="Preset">
              <div style={{ fontSize: 13, color: PALETTE.ink2 }}>
                <strong>{campaign?.offer || 'campaign'}</strong> → <code>{preset}</code>
              </div>
            </Section>

            <Section title="Identity">
              <Field label="Phone" required>
                <Input value={quiz.phone} onChange={(v) => setQuiz({ ...quiz, phone: v })} placeholder="(613) 555-0100" />
              </Field>
              <Field label="Location" required>
                <Input value={quiz.location} onChange={(v) => setQuiz({ ...quiz, location: v })} placeholder="Ottawa, ON" />
              </Field>
              <Field label="Service area" hint="Comma or line separated" required>
                <Input value={quiz.serviceArea} onChange={(v) => setQuiz({ ...quiz, serviceArea: v })} placeholder="Ottawa" />
              </Field>
            </Section>

            <Section title="Logo" hint="Exactly one approved logo image is required">
              {images.length === 0 ? (
                <p style={mutedStyle}>
                  No approved logo image was found for this campaign. Upload one below.
                </p>
              ) : (
                <select
                  value={quiz.logoArtifactId}
                  onChange={(event) => setQuiz({ ...quiz, logoArtifactId: event.target.value })}
                  style={inputStyle}
                >
                  <option value="">Select a logo…</option>
                  {images.map((image) => (
                    <option key={image.id} value={image.id}>
                      {image.metadata?.label || image.metadata?.alt || image.id}
                    </option>
                  ))}
                </select>
              )}
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 12, color: PALETTE.ink2 }}>
                  {images.length === 0 ? 'Upload a logo' : 'Or upload a new logo'} — PNG, JPEG, or WebP (max 700 KB)
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={uploadState === 'uploading'}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) uploadLogo(file);
                  }}
                  style={{ fontSize: 13, color: PALETTE.ink2 }}
                />
              </label>
              {uploadState === 'uploading' && <p style={mutedStyle}>Uploading logo…</p>}
              {uploadError && (
                <p style={{ ...mutedStyle, color: PALETTE.warn }}>{safeFailureMessage(uploadError, 'campaign-review')}</p>
              )}
            </Section>

            <Section title="Quiz options" hint="One answer per line, at least two per question">
              <Field label="Project scope" required>
                <TextArea value={quiz.projectScope} onChange={(v) => setQuiz({ ...quiz, projectScope: v })} rows={3} />
              </Field>
              <Field label="Timeline" required>
                <TextArea value={quiz.timeline} onChange={(v) => setQuiz({ ...quiz, timeline: v })} rows={3} />
              </Field>
              <Field label="Budget band" required>
                <TextArea value={quiz.budgetBand} onChange={(v) => setQuiz({ ...quiz, budgetBand: v })} rows={3} />
              </Field>
            </Section>

            <Section title="Qualification rule" hint="Disqualifies one answer; the value must match a quiz option above">
              <Field label="Field">
                <select
                  value={quiz.ruleField}
                  onChange={(event) => setQuiz({ ...quiz, ruleField: event.target.value })}
                  style={inputStyle}
                >
                  <option value="projectScope">projectScope</option>
                  <option value="timeline">timeline</option>
                  <option value="budgetBand">budgetBand</option>
                </select>
              </Field>
              <Field label="Disqualifying value" required>
                <Input value={quiz.ruleValue} onChange={(v) => setQuiz({ ...quiz, ruleValue: v })} />
              </Field>
            </Section>

            <Section title="Claims" hint="Optional. One per line: label | type | source">
              <TextArea
                value={quiz.claims}
                onChange={(v) => setQuiz({ ...quiz, claims: v })}
                rows={3}
                placeholder="Local renovation team | trust | Approved company profile"
              />
            </Section>

            <Section title="Copy">
              <Field label="Primary CTA label" required>
                <Input value={quiz.primaryLabel} onChange={(v) => setQuiz({ ...quiz, primaryLabel: v })} />
              </Field>
              <Field label="Consent label" required>
                <TextArea value={quiz.consentLabel} onChange={(v) => setQuiz({ ...quiz, consentLabel: v })} rows={2} />
              </Field>
              <Field label="SEO title">
                <Input value={quiz.seoTitle} onChange={(v) => setQuiz({ ...quiz, seoTitle: v })} />
              </Field>
              <Field label="SEO description">
                <Input value={quiz.seoDescription} onChange={(v) => setQuiz({ ...quiz, seoDescription: v })} />
              </Field>
            </Section>

            {isFinancing && (
              <Section title="Financing content" hint="Entered by you. Never fabricated — use your real financing partner's terms.">
                <Field label="Hero headline" required>
                  <Input value={financing.heroHeadline} onChange={(v) => setFinancing({ ...financing, heroHeadline: v })} />
                </Field>
                <Field label="Hero body" required>
                  <TextArea value={financing.heroBody} onChange={(v) => setFinancing({ ...financing, heroBody: v })} rows={2} />
                </Field>
                <Field label="Payment heading" required>
                  <Input value={financing.paymentHeading} onChange={(v) => setFinancing({ ...financing, paymentHeading: v })} />
                </Field>
                <Field label="Payment body">
                  <TextArea value={financing.paymentBody} onChange={(v) => setFinancing({ ...financing, paymentBody: v })} rows={2} />
                </Field>
                <Field label="Payment terms" required>
                  <TextArea value={financing.paymentTerms} onChange={(v) => setFinancing({ ...financing, paymentTerms: v })} rows={2} />
                </Field>
                <Field label="Payment examples" hint="At least two. One per line: label | value | detail" required>
                  <TextArea
                    value={financing.paymentExamples}
                    onChange={(v) => setFinancing({ ...financing, paymentExamples: v })}
                    rows={3}
                    placeholder="$40,000 project | $550/mo | 84 months at 7.99% APR"
                  />
                </Field>
                <Field label="Benefits" hint="One per line" required>
                  <TextArea value={financing.benefits} onChange={(v) => setFinancing({ ...financing, benefits: v })} rows={2} />
                </Field>
                <Field label="FAQs" hint="At least one. One per line: question | answer" required>
                  <TextArea value={financing.faqs} onChange={(v) => setFinancing({ ...financing, faqs: v })} rows={3} />
                </Field>
                <Field label="Disclosure" required>
                  <TextArea value={financing.disclosure} onChange={(v) => setFinancing({ ...financing, disclosure: v })} rows={2} />
                </Field>
                <Field label="Legal paragraphs" hint="At least one. One per line" required>
                  <TextArea value={financing.legalParagraphs} onChange={(v) => setFinancing({ ...financing, legalParagraphs: v })} rows={3} />
                </Field>
              </Section>
            )}

            {submitError && (
              <div style={{ padding: 12, background: '#FFF8EA', border: `1px solid ${PALETTE.line}`, borderRadius: 8, color: PALETTE.warn, fontSize: 13 }}>
                {safeFailureMessage(submitError, 'campaign-review')}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={onClose} disabled={submitState === 'submitting'}>Cancel</Button>
              <Button
                variant="accent"
                size="sm"
                onClick={submit}
                disabled={submitState === 'submitting' || uploadState === 'uploading' || !quiz.logoArtifactId}
              >
                {submitState === 'submitting' ? 'Saving…' : 'Approve & save presentation'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: PALETTE.ink3, marginTop: 2 }}>{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: PALETTE.ink2 }}>
        {label}{required && <span style={{ color: PALETTE.warn }}> *</span>}
        {hint && <span style={{ color: PALETTE.ink3 }}> — {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

function TextArea({ value, onChange, rows = 3, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'Inter, sans-serif' }}
    />
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(26,23,20,0.4)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'clamp(16px, 5vh, 48px) 16px',
  overflowY: 'auto',
  zIndex: 50,
};

const panelStyle = {
  width: '100%',
  maxWidth: 640,
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 14,
  padding: 'clamp(18px, 3vw, 28px)',
  fontFamily: 'Inter, sans-serif',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 20,
};

const eyebrowStyle = {
  fontSize: 11,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
  marginBottom: 6,
};

const titleStyle = {
  fontFamily: 'Instrument Serif, serif',
  fontStyle: 'italic',
  fontSize: 28,
  fontWeight: 400,
  margin: 0,
  color: PALETTE.ink,
};

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  fontSize: 13,
  color: PALETTE.ink,
  background: PALETTE.surface,
  boxSizing: 'border-box',
};

const mutedStyle = { fontSize: 13, color: PALETTE.ink3 };
